import { useState } from 'react';
import { ChevronDown, LogOut, Moon, Settings, Sun } from 'lucide-react';
import { toast } from 'sonner';
import { accountInitials, accountLabel } from '../account';
import type { Session } from '../api';
import { Avatar, AvatarFallback } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Separator } from './ui/separator';

/**
 * The account menu: who is signed in, the theme toggle and Sign out. The desktop variant replaces the sidebar
 * profile row; the mobile variant lives in the top bar so sign out exists at every viewport. Radix owns the
 * keyboard behaviour (Enter/Space opens, Escape closes, focus returns to the trigger).
 */
export function AccountMenu({ session, dark, onToggleTheme, onSignOut, variant }: {
  session: Session; dark: boolean; onToggleTheme: () => void; onSignOut: () => Promise<void>; variant: 'desktop' | 'mobile';
}) {
  const [signingOut, setSigningOut] = useState(false);
  const name = session.userName?.trim() || 'Alder user';
  const detail = accountLabel(session.actor.roles[0], session.company);
  const initials = accountInitials(name);

  async function signOut() {
    if (signingOut) return;
    setSigningOut(true);
    try { await onSignOut(); }
    catch (error) { toast.error(error instanceof Error && error.message ? error.message : 'Could not sign out. Please try again.'); }
    finally { setSigningOut(false); }
  }

  const trigger = variant === 'mobile'
    ? <button type="button" className="account-trigger account-trigger-mobile" aria-label={`Account menu for ${name}`}>
        <Avatar size="sm"><AvatarFallback className="account-avatar">{initials}</AvatarFallback></Avatar>
      </button>
    : <button type="button" className="profile-row account-trigger account-trigger-desktop">
        <Avatar><AvatarFallback className="account-avatar">{initials}</AvatarFallback></Avatar>
        <span className="account-identity" title={`${name} · ${detail}`}><strong>{name}</strong><small>{detail}</small></span>
        <ChevronDown className="account-chevron" size={14} aria-hidden />
      </button>;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent className="account-menu" align={variant === 'desktop' ? 'start' : 'end'} sideOffset={8} aria-label="Account menu">
        <DropdownMenuLabel className="account-menu-head">
          <Avatar><AvatarFallback className="account-avatar">{initials}</AvatarFallback></Avatar>
          <span className="account-identity"><strong>{name}</strong><small>{detail}</small></span>
        </DropdownMenuLabel>
        <Separator />
        <DropdownMenuItem className="account-menu-item" disabled={signingOut} onSelect={() => { window.location.hash = 'settings'; }}>
          <Settings aria-hidden />
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem className="account-menu-item" disabled={signingOut} onSelect={() => onToggleTheme()}>
          {dark ? <Sun aria-hidden /> : <Moon aria-hidden />}
          {dark ? 'Light appearance' : 'Dark appearance'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="account-menu-item" variant="destructive" disabled={signingOut}
          onSelect={event => { event.preventDefault(); void signOut(); }}>
          <LogOut aria-hidden />
          {signingOut ? 'Signing out…' : 'Sign out'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
